import React, { useState, useEffect } from 'react';
import {
  FiEdit, FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiX, FiCheck,
  FiChevronLeft, FiChevronRight, FiEye, FiFileText, FiCheckCircle, FiXCircle
} from 'react-icons/fi';
import { blogPublicApi, blogAdminApi, blogUserApi } from '../services/blogApi';
import { Blog, BlogStatus } from '../types/blog';
import './BlogManagement.css';

const Icon = ({ icon: IconComponent, className }: { icon: any; className?: string }) =>
  React.createElement(IconComponent as any, { className });

const BlogManagement: React.FC = () => {
  const [activeView, setActiveView] = useState<'all' | 'pending'>('all');
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [pendingBlogs, setPendingBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<BlogStatus | ''>('');
  
  // Modals
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [showBlogView, setShowBlogView] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  
  // Forms
  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    summary: '',
    tags: '',
  });
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);
  
  const [approveComment, setApproveComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadData();
  }, [activeView, currentPage, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeView === 'pending') {
        const data = await blogAdminApi.getPending();
        setPendingBlogs(data);
      } else {
        const data = await blogAdminApi.getAll(currentPage, 20, searchTerm, statusFilter || undefined);
        setBlogs(data.content);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedBlog) return;
    try {
      await blogAdminApi.approve(selectedBlog.id, approveComment ? { comment: approveComment } : undefined);
      setShowApproveModal(false);
      setSelectedBlog(null);
      setApproveComment('');
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleReject = async () => {
    if (!selectedBlog || !rejectReason) {
      alert('Please provide a rejection reason');
      return;
    }
    try {
      await blogAdminApi.reject(selectedBlog.id, { reason: rejectReason });
      setShowRejectModal(false);
      setSelectedBlog(null);
      setRejectReason('');
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      await blogAdminApi.delete(id);
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!blogForm.title || !blogForm.content) {
      alert('Please fill in title and content');
      return;
    }
    try {
      const tagsArray = blogForm.tags ? blogForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : [];
      const blogData = {
        title: blogForm.title.trim(),
        content: blogForm.content.trim(),
        summary: blogForm.summary?.trim() || undefined,
        tags: tagsArray.length > 0 ? tagsArray : undefined,
      };
      
      if (editingBlog) {
        await blogAdminApi.update(editingBlog.id, blogData);
      } else {
        await blogUserApi.create(blogData, selectedImages.length > 0 ? selectedImages : undefined, selectedDocuments.length > 0 ? selectedDocuments : undefined);
      }
      
      setShowBlogModal(false);
      setEditingBlog(null);
      setBlogForm({ title: '', content: '', summary: '', tags: '' });
      setSelectedImages([]);
      setSelectedDocuments([]);
      loadData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const openEditBlog = (blog: Blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title,
      content: blog.content,
      summary: blog.summary || '',
      tags: blog.tags ? blog.tags.join(', ') : '',
    });
    setSelectedImages([]);
    setSelectedDocuments([]);
    setShowBlogModal(true);
  };

  const getStatusBadgeClass = (status: BlogStatus) => {
    switch (status) {
      case 'PUBLISHED': return 'status-published';
      case 'PENDING_APPROVAL': return 'status-pending';
      case 'REJECTED': return 'status-rejected';
      case 'DRAFT': return 'status-draft';
      default: return 'status-default';
    }
  };

  return (
    <div className="blog-management">
      <div className="bm-header">
        <div className="bm-header-left">
          <Icon icon={FiEdit} className="bm-header-icon" />
          <div>
            <h2>Blog Management</h2>
            <p>Manage blog posts and approvals</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-primary" onClick={() => { setEditingBlog(null); setBlogForm({ title: '', content: '', summary: '', tags: '' }); setSelectedImages([]); setSelectedDocuments([]); setShowBlogModal(true); }}>
            <Icon icon={FiPlus} /> Create Blog
          </button>
          <button className="btn-refresh" onClick={loadData} disabled={loading}>
            <Icon icon={FiRefreshCw} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      <div className="bm-tabs">
        <button
          className={`bm-tab ${activeView === 'all' ? 'active' : ''}`}
          onClick={() => setActiveView('all')}
        >
          <Icon icon={FiFileText} /> All Blogs
        </button>
        <button
          className={`bm-tab ${activeView === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveView('pending')}
        >
          <Icon icon={FiCheckCircle} /> Pending Approval ({pendingBlogs.length})
        </button>
      </div>

      <div className="bm-content">
        {activeView === 'all' && (
          <div className="bm-filters">
            <div className="bm-search-box">
              <Icon icon={FiSearch} />
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && loadData()}
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value as BlogStatus | ''); setCurrentPage(0); }}
            >
              <option value="">All Statuses</option>
              <option value="PUBLISHED">Published</option>
              <option value="PENDING_APPROVAL">Pending</option>
              <option value="REJECTED">Rejected</option>
              <option value="DRAFT">Draft</option>
            </select>
            <button className="btn-primary" onClick={loadData}>
              <Icon icon={FiSearch} /> Search
            </button>
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <Icon icon={FiRefreshCw} className="spinning" />
            <p>Loading blogs...</p>
          </div>
        ) : (activeView === 'pending' ? pendingBlogs : blogs).length === 0 ? (
          <div className="empty-state">
            <Icon icon={FiFileText} />
            <p>No blogs found</p>
          </div>
        ) : (
          <>
            <div className="bm-blogs-list">
              {(activeView === 'pending' ? pendingBlogs : blogs).map(blog => (
                <div key={blog.id} className="bm-blog-card">
                  <div className="bm-card-header">
                    <h4>{blog.title}</h4>
                    <span className={`status-badge ${getStatusBadgeClass(blog.status)}`}>
                      {blog.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="bm-card-summary">{blog.summary || blog.content.substring(0, 150) + '...'}</p>
                  <div className="bm-card-meta">
                    <span>Author: {blog.author.name}</span>
                    <span>{blog.views} views</span>
                    {blog.tags && blog.tags.length > 0 && (
                      <span>Tags: {blog.tags.join(', ')}</span>
                    )}
                  </div>
                  {blog.rejectedReason && (
                    <div className="bm-rejection-reason">
                      <strong>Rejection Reason:</strong> {blog.rejectedReason}
                    </div>
                  )}
                  <div className="bm-card-actions">
                    <button className="btn-icon" onClick={() => { setSelectedBlog(blog); setShowBlogView(true); }}>
                      <Icon icon={FiEye} />
                    </button>
                    <button className="btn-icon" onClick={() => openEditBlog(blog)}>
                      <Icon icon={FiEdit2} />
                    </button>
                    {blog.status === 'PENDING_APPROVAL' && (
                      <>
                        <button
                          className="btn-icon btn-approve"
                          onClick={() => { setSelectedBlog(blog); setShowApproveModal(true); }}
                        >
                          <Icon icon={FiCheckCircle} />
                        </button>
                        <button
                          className="btn-icon btn-reject"
                          onClick={() => { setSelectedBlog(blog); setShowRejectModal(true); }}
                        >
                          <Icon icon={FiXCircle} />
                        </button>
                      </>
                    )}
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(blog.id)}>
                      <Icon icon={FiTrash2} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {activeView === 'all' && totalPages > 1 && (
              <div className="pagination">
                <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>
                  <Icon icon={FiChevronLeft} />
                </button>
                <span>Page {currentPage + 1} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>
                  <Icon icon={FiChevronRight} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Blog View Modal */}
      {showBlogView && selectedBlog && (
        <div className="modal-overlay" onClick={() => { setShowBlogView(false); setSelectedBlog(null); }}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedBlog.title}</h3>
              <button className="btn-close" onClick={() => { setShowBlogView(false); setSelectedBlog(null); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <div className="blog-view">
                <div className="blog-meta">
                  <span>Author: {selectedBlog.author.name}</span>
                  <span>Status: {selectedBlog.status}</span>
                  <span>Views: {selectedBlog.views}</span>
                  {selectedBlog.publishedAt && <span>Published: {new Date(selectedBlog.publishedAt).toLocaleDateString()}</span>}
                </div>
                {selectedBlog.summary && <p className="blog-summary">{selectedBlog.summary}</p>}
                <div className="blog-content">{selectedBlog.content}</div>
                {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                  <div className="blog-tags">
                    <strong>Tags:</strong> {selectedBlog.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowBlogView(false); setSelectedBlog(null); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedBlog && (
        <div className="modal-overlay" onClick={() => { setShowApproveModal(false); setSelectedBlog(null); setApproveComment(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve Blog</h3>
              <button className="btn-close" onClick={() => { setShowApproveModal(false); setSelectedBlog(null); setApproveComment(''); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to approve "{selectedBlog.title}"?</p>
              <div className="form-group">
                <label>Comment (optional)</label>
                <textarea
                  value={approveComment}
                  onChange={e => setApproveComment(e.target.value)}
                  rows={3}
                  placeholder="Add a comment..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowApproveModal(false); setSelectedBlog(null); setApproveComment(''); }}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleApprove}>
                <Icon icon={FiCheckCircle} /> Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedBlog && (
        <div className="modal-overlay" onClick={() => { setShowRejectModal(false); setSelectedBlog(null); setRejectReason(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Blog</h3>
              <button className="btn-close" onClick={() => { setShowRejectModal(false); setSelectedBlog(null); setRejectReason(''); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reject "{selectedBlog.title}"?</p>
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  rows={4}
                  placeholder="Please provide a reason for rejection..."
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowRejectModal(false); setSelectedBlog(null); setRejectReason(''); }}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleReject} disabled={!rejectReason.trim()}>
                <Icon icon={FiXCircle} /> Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Blog Modal */}
      {showBlogModal && (
        <div className="modal-overlay" onClick={() => { setShowBlogModal(false); setEditingBlog(null); setBlogForm({ title: '', content: '', summary: '', tags: '' }); setSelectedImages([]); setSelectedDocuments([]); }}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingBlog ? 'Edit Blog' : 'Create Blog'}</h3>
              <button className="btn-close" onClick={() => { setShowBlogModal(false); setEditingBlog(null); setBlogForm({ title: '', content: '', summary: '', tags: '' }); setSelectedImages([]); setSelectedDocuments([]); }}>
                <Icon icon={FiX} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={blogForm.title}
                  onChange={e => setBlogForm({ ...blogForm, title: e.target.value })}
                  placeholder="Enter blog title..."
                  required
                />
              </div>
              <div className="form-group">
                <label>Summary</label>
                <textarea
                  value={blogForm.summary}
                  onChange={e => setBlogForm({ ...blogForm, summary: e.target.value })}
                  rows={3}
                  placeholder="Enter blog summary..."
                />
              </div>
              <div className="form-group">
                <label>Content *</label>
                <textarea
                  value={blogForm.content}
                  onChange={e => setBlogForm({ ...blogForm, content: e.target.value })}
                  rows={12}
                  placeholder="Enter blog content..."
                  required
                />
              </div>
              <div className="form-group">
                <label>Tags (comma-separated)</label>
                <input
                  type="text"
                  value={blogForm.tags}
                  onChange={e => setBlogForm({ ...blogForm, tags: e.target.value })}
                  placeholder="e.g., technology, programming, tips"
                />
              </div>
              {!editingBlog && (
                <>
                  <div className="form-group">
                    <label>Images (optional)</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setSelectedImages(files);
                      }}
                    />
                    {selectedImages.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                        {selectedImages.length} image(s) selected
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Documents (optional)</label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx"
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setSelectedDocuments(files);
                      }}
                    />
                    {selectedDocuments.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                        {selectedDocuments.length} document(s) selected
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowBlogModal(false); setEditingBlog(null); setBlogForm({ title: '', content: '', summary: '', tags: '' }); setSelectedImages([]); setSelectedDocuments([]); }}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateOrUpdate} disabled={!blogForm.title || !blogForm.content}>
                <Icon icon={FiCheck} /> {editingBlog ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;

